import api from './api';

const MAX_PAGE_SIZE = 100;

const fetchWmiCodes = async ({ page = 1, size = 25, search = null } = {}) => {
  try {
    const requestParams = {
      'page[number]': page,
      'page[size]': size,
      'filter[oem_name_cont]': search,
    };

    const filteredRequestParams = Object.fromEntries(
      Object.entries(requestParams).filter(
        ([_key, value]) => value !== null && value !== '',
      ),
    );

    const response = await api.get('/wmi-codes', {
      params: filteredRequestParams,
    });

    const { data, meta } = response.data;

    return { data, totalRecords: meta?.total_records };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

// Walks the paginated endpoint (page[size] is capped at 100 by the backend) and
// returns the full collection. For screens that need every record — e.g. the
// OEM options in execution conditions — not for paginated list views.
const fetchAllWmiCodes = async () => {
  const pageParams = (page) => ({
    'page[number]': page,
    'page[size]': MAX_PAGE_SIZE,
  });
  try {
    const first = await api.get('/wmi-codes', { params: pageParams(1) });
    const firstRows = first.data.data || [];
    const totalRecords = first.data.meta?.total_records ?? firstRows.length;
    const totalPages = Math.ceil(totalRecords / MAX_PAGE_SIZE);

    const rest = await Promise.all(
      Array.from({ length: Math.max(totalPages - 1, 0) }, (_v, i) =>
        api.get('/wmi-codes', { params: pageParams(i + 2) }),
      ),
    );
    const all = rest.reduce(
      (acc, res) => acc.concat(res.data.data || []),
      firstRows,
    );
    return { data: all, totalRecords };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchWmiCode = async (uuid) => {
  try {
    const response = await api.get(`/wmi-codes/${uuid}`);
    const { data } = response.data;

    return { data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const createWmiCode = async (attributes) => {
  try {
    const response = await api.post('/wmi-codes', {
      data: { type: 'wmi_codes', attributes },
    });
    const { data } = response.data;

    return { data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const updateWmiCode = async (uuid, attributes) => {
  try {
    const response = await api.put(`/wmi-codes/${uuid}`, {
      data: { type: 'wmi_codes', attributes },
    });
    const { data } = response.data;

    return { data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchWmiCodeChangeHistory = async (uuid) => {
  try {
    const response = await api.get(`/wmi-codes/${uuid}/change-history`);
    const { data } = response.data;

    return { data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const deleteWmiCode = async (uuid) => {
  try {
    await api.delete(`/wmi-codes/${uuid}`, { data: {} });

    return { success: true };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

export default {
  fetchWmiCodes,
  fetchAllWmiCodes,
  fetchWmiCode,
  createWmiCode,
  updateWmiCode,
  deleteWmiCode,
  fetchWmiCodeChangeHistory,
};
